import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SearchFiltersState } from "@/components/venues/SearchFilters";

const PAGE_SIZE = 12;

/** Remove diacritics for accent-insensitive matching */
function removeAccents(str: string): string {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function useVenues(filters: SearchFiltersState, page: number) {
  return useQuery({
    queryKey: ["venues", filters, page],
    queryFn: async () => {
      const needsJsPagination = !!filters.maxCommunityPrice;

      let query = supabase
        .from("venues")
        .select("*", { count: "exact" })
        .eq("status", "active");

      // --- Server-side filters ---

      // Name: partial, case-insensitive (accent filtering done post-query in JS)
      if (filters.name) {
        query = query.ilike("name", `%${filters.name}%`);
      }

      // City: partial, case-insensitive
      if (filters.city) {
        query = query.ilike("city", `%${filters.city}%`);
      }

      // Community: exact match, case-insensitive (no wildcards)
      if (filters.community && filters.community !== "all") {
        query = query.ilike("autonomous_community", filters.community);
      }

      // Type: exact match
      if (filters.type && filters.type !== "all") {
        query = query.eq("type", filters.type as any);
      }

      // Capacity: NULLs are excluded automatically by .gte()
      if (filters.minCapacity) {
        const minCap = parseInt(filters.minCapacity);
        if (minCap > 0) {
          query = query.gte("capacity", minCap);
        }
      }

      // Official price: NULLs are excluded automatically by .lte()
      if (filters.maxOfficialPrice) {
        const maxOP = parseInt(filters.maxOfficialPrice);
        if (maxOP >= 0) {
          query = query.lte("official_price", maxOP);
        }
      }

      // Sorting
      const sortMap: Record<string, string> = {
        name: "name",
        city: "city",
        capacity: "capacity",
        price: "official_price",
      };
      const sortCol = sortMap[filters.sortBy] || "name";
      query = query.order(sortCol, { ascending: sortCol !== "capacity", nullsFirst: false });

      // Pagination: skip DB pagination when we need JS filtering for community price
      if (!needsJsPagination) {
        const from = page * PAGE_SIZE;
        query = query.range(from, from + PAGE_SIZE - 1);
      }

      const { data, error, count } = await query;
      if (error) throw error;

      let venuesWithStats = data || [];

      // --- Fetch review stats + main image + rates ---
      if (venuesWithStats.length > 0) {
        const venueIds = venuesWithStats.map((v) => v.id);

        const [reviewsRes, imagesRes, ratesRes] = await Promise.all([
          supabase
            .from("reviews")
            .select("venue_id, overall_rating, price_paid")
            .in("venue_id", venueIds)
            .eq("status", "approved"),
          supabase
            .from("venue_images")
            .select("venue_id, image_url, is_main, sort_order")
            .in("venue_id", venueIds)
            .order("sort_order", { ascending: true }),
          supabase
            .from("venue_rates" as any)
            .select("venue_id, price, is_active")
            .in("venue_id", venueIds)
            .eq("is_active", true),
        ]);

        const statsMap = new Map<string, { ratings: number[]; prices: number[] }>();
        reviewsRes.data?.forEach((r) => {
          if (!statsMap.has(r.venue_id)) {
            statsMap.set(r.venue_id, { ratings: [], prices: [] });
          }
          const s = statsMap.get(r.venue_id)!;
          if (r.overall_rating) s.ratings.push(r.overall_rating);
          if (r.price_paid) s.prices.push(r.price_paid);
        });

        const imageMap = new Map<string, string>();
        imagesRes.data?.forEach((img) => {
          if (!imageMap.has(img.venue_id) || img.is_main) {
            imageMap.set(img.venue_id, img.image_url);
          }
        });

        // Build rates map: venue_id -> { min_price, count }
        const ratesMap = new Map<string, { minPrice: number; count: number }>();
        (ratesRes.data as any[])?.forEach((r: any) => {
          const existing = ratesMap.get(r.venue_id);
          const p = Number(r.price);
          if (!existing) {
            ratesMap.set(r.venue_id, { minPrice: p, count: 1 });
          } else {
            ratesMap.set(r.venue_id, { minPrice: Math.min(existing.minPrice, p), count: existing.count + 1 });
          }
        });

        venuesWithStats = venuesWithStats.map((v) => {
          const s = statsMap.get(v.id);
          const rateInfo = ratesMap.get(v.id);
          const reference_price = rateInfo ? rateInfo.minPrice : v.official_price;
          return {
            ...v,
            avg_rating: s?.ratings.length
              ? s.ratings.reduce((a, b) => a + b, 0) / s.ratings.length
              : null,
            review_count: s?.ratings.length ?? 0,
            estimated_price: s?.prices.length
              ? s.prices.reduce((a, b) => a + b, 0) / s.prices.length
              : null,
            main_image: imageMap.get(v.id) ?? null,
            reference_price,
            has_multiple_rates: (rateInfo?.count ?? 0) > 1,
          };
        });
      }

      // --- Post-query JS filters ---

      // Accent-aware name filter: re-filter in JS to catch accent mismatches
      if (filters.name) {
        const normalizedSearch = removeAccents(filters.name.toLowerCase());
        venuesWithStats = venuesWithStats.filter((v: any) =>
          removeAccents(v.name.toLowerCase()).includes(normalizedSearch)
        );
      }

      // Community price: filter in JS (calculated field, not in DB)
      // Venues without estimated_price are excluded when this filter is active
      if (filters.maxCommunityPrice) {
        const maxCP = parseInt(filters.maxCommunityPrice);
        venuesWithStats = venuesWithStats.filter(
          (v: any) => v.estimated_price != null && v.estimated_price <= maxCP
        );
      }

      // --- Compute totals and paginate ---
      if (needsJsPagination) {
        const totalCount = venuesWithStats.length;
        const from = page * PAGE_SIZE;
        const paginatedVenues = venuesWithStats.slice(from, from + PAGE_SIZE);
        return {
          venues: paginatedVenues,
          totalCount,
          totalPages: Math.ceil(totalCount / PAGE_SIZE),
        };
      }

      return {
        venues: venuesWithStats,
        totalCount: count ?? 0,
        totalPages: Math.ceil((count ?? 0) / PAGE_SIZE),
      };
    },
  });
}
